import React, { useCallback, useEffect } from 'react';
import FundedInvestingManualKycDialog from './fundedInvestingManualKycDialog.render';
import { KycDialogOpen } from 'features/prop/interfaces';
import {
    prop__myPlans_acceptChallengeTermsAndCondition,
    prop__myPlans_updateStatusManualKyc,
    prop_handleChallengeStartFundedAlert,
    prop_handleKycDialogState,
} from 'features/prop/store/plans/actions';
import { user_refreshUserData } from 'store/user/actions';
import { connect } from 'react-redux';
import {
    prop_myPlans_isKycDialogOpen,
    prop_myPlans_manualKycLoading,
    prop_myPlans_getChallengeById,
} from 'features/prop/store/plans/selectors';
import { user_userInfo } from 'store/user/selectors';
import {
    AccountInfoParsed,
    AccountInfoParsedKycVerificationStatusEnum,
    ManualKycRequest,
    PropChallengeChallengeTypeEnum,
} from 'services/generatedClientFromSwagger';
import { RootState } from 'store';
import { getAuditionIndexInPlan } from 'features/prop/utils/auditionChallenges';

export interface FundedInvestingManualKycDialogPropsWithStoreConnectionProps {
    isDialogOpen: KycDialogOpen;
    handleKycDialogState: (isDialogOpen: KycDialogOpen) => void;
    acceptChallengeTermsAndCondition: (challengeId: string) => void;
    userInfo: AccountInfoParsed;
    updateStatusManualKyc: (manualKycRequest: ManualKycRequest) => void;
    manualKycLoading: boolean;
    handleChallengeStartFundedAlert: (challengeId: string, isOpen: boolean, sent: boolean) => Promise<void>;
    refreshUserData: () => void;
    isFundedChallenge: boolean;
    requireKyc: boolean;
    isFirstAuditionChallenge: boolean;
}

export const mapDispatchToProps = {
    handleKycDialogState: prop_handleKycDialogState,
    acceptChallengeTermsAndCondition: prop__myPlans_acceptChallengeTermsAndCondition,
    updateStatusManualKyc: prop__myPlans_updateStatusManualKyc,
    handleChallengeStartFundedAlert: prop_handleChallengeStartFundedAlert,
    refreshUserData: user_refreshUserData,
};

export const mapStateToProps = (state: RootState) => {
    const isDialogOpen = prop_myPlans_isKycDialogOpen(state);
    const challenge = prop_myPlans_getChallengeById(state, isDialogOpen?.id);

    const plans = state.prop_myPlans?.plans || [];
    const planForChallenge = plans.find((plan) =>
        plan?.propChallenges?.some((planChallenge) => planChallenge?.id === isDialogOpen?.id)
    );
    const auditionIndex = getAuditionIndexInPlan(planForChallenge, isDialogOpen?.id);
    const isFirstAuditionChallenge = auditionIndex === 0;
    const isFundedChallenge = challenge?.challengeType === PropChallengeChallengeTypeEnum.Funded;

    // For backward compatibility: funded challenges default to requiring KYC (true),
    // audition challenges default to not requiring KYC (false)
    const requireKyc = challenge?.requireKyc ?? isFundedChallenge;

    return {
        isDialogOpen,
        userInfo: user_userInfo(state),
        manualKycLoading: prop_myPlans_manualKycLoading(state),
        isFundedChallenge,
        requireKyc,
        isFirstAuditionChallenge,
    };
};

const FundedInvestingManualKycDialogWithStoreConnection: React.FunctionComponent<
    FundedInvestingManualKycDialogPropsWithStoreConnectionProps
> = ({
    handleKycDialogState,
    isDialogOpen,
    acceptChallengeTermsAndCondition,
    userInfo,
    updateStatusManualKyc,
    manualKycLoading,
    handleChallengeStartFundedAlert,
    refreshUserData,
    isFundedChallenge,
    requireKyc,
    isFirstAuditionChallenge,
}) => {
    useEffect(() => {
        if (!isDialogOpen?.id) return;
        refreshUserData();
    }, [isDialogOpen?.id, refreshUserData]);

    const hasCompletedKyc =
        userInfo.kycVerificationStatus === AccountInfoParsedKycVerificationStatusEnum.Approved ||
        userInfo.kycVerificationStatus === AccountInfoParsedKycVerificationStatusEnum.TraderAgreementAccepted;

    // Determine if this audition should auto-activate:
    // 1. KYC not required for this challenge (!requireKyc), OR
    // 2. KYC required but already completed (requireKyc && hasCompletedKyc)
    const shouldAutoActivateAudition = Boolean(isDialogOpen && !isFundedChallenge && (!requireKyc || hasCompletedKyc));

    // Auto-activate audition challenges when no dialog is needed
    useEffect(() => {
        if (!shouldAutoActivateAudition || !isDialogOpen) {
            return;
        }

        // No dialog needed for audition - just show alert and close dialog state
        handleChallengeStartFundedAlert(isDialogOpen.id, true, true);
        handleKycDialogState(undefined);
    }, [shouldAutoActivateAudition, isDialogOpen, handleChallengeStartFundedAlert, handleKycDialogState]);

    const onAccept = useCallback(() => {
        if (isDialogOpen) {
            acceptChallengeTermsAndCondition(isDialogOpen.id);
        }
        handleChallengeStartFundedAlert(isDialogOpen?.id ?? '', true, true);
        handleKycDialogState(undefined);
    }, [acceptChallengeTermsAndCondition, handleChallengeStartFundedAlert, handleKycDialogState, isDialogOpen]);

    const onClose = useCallback(() => {
        handleChallengeStartFundedAlert(isDialogOpen?.id ?? '', true, false);
        handleKycDialogState(undefined);
    }, [handleChallengeStartFundedAlert, handleKycDialogState, isDialogOpen?.id]);

    // Don't render dialog if auto-activating (must be after all hooks)
    if (shouldAutoActivateAudition) {
        return null;
    }

    return (
        <FundedInvestingManualKycDialog
            key={`FundedInvestingManualKycDialog_${isDialogOpen?.id}`}
            manualKycLoading={manualKycLoading}
            userInfo={userInfo}
            isOpen={!!isDialogOpen}
            onClose={onClose}
            onAccept={onAccept}
            isFundedChallenge={isFundedChallenge}
            requireKyc={requireKyc}
            isFirstAuditionChallenge={isFirstAuditionChallenge}
            updateStatusManualKyc={updateStatusManualKyc}
            acceptChallengeTermsAndCondition={acceptChallengeTermsAndCondition}
            challengeId={isDialogOpen?.id ?? ''}
        />
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(FundedInvestingManualKycDialogWithStoreConnection);
